/**
 * SIL-2333: customer-supplied notes and file attachments on a bench run.
 * Customer-supplied, NOT verified, and outside the signed record / evidence hash.
 */
import { z } from 'zod';

export const BENCH_NOTES_MAX_CHARS = 4000;
export const BENCH_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
export const BENCH_ATTACHMENT_MAX_PER_RUN = 20;
/** PDF, JPEG, PNG only. Never html, svg or octet-stream. */
export const BENCH_ATTACHMENT_CONTENT_TYPES: Readonly<Record<string, string>> = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
};

// Built from a string so the line separators U+2028/U+2029 stay escaped in the source.
const CONTROL_CHARS = new RegExp('[\\u0000-\\u0009\\u000B\\u000C\\u000E-\\u001F\\u007F-\\u009F\\u2028\\u2029]', 'g');

/** Strip control characters except newline; CR and CRLF become LF. */
export function sanitizeBenchText(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    // eslint-disable-next-line no-control-regex
    .replace(CONTROL_CHARS, '');
}

/** Body of PATCH /api/client/bench/runs/:id/notes. A blank note clears it. */
export const benchRunNotesRequestSchema = z.object({
  notes: z.string().transform(sanitizeBenchText).pipe(z.string().max(BENCH_NOTES_MAX_CHARS)),
});
export type BenchRunNotesRequest = z.input<typeof benchRunNotesRequestSchema>;

/** Body of POST /api/client/bench/runs/:id/attachments (presign request). */
export const benchAttachmentPresignRequestSchema = z.object({
  fileName: z
    .string()
    .transform((s) => sanitizeBenchText(s).replace(/[\n/\\]/g, '_').trim())
    .pipe(z.string().min(1).max(255)),
  contentType: z.string().refine((t) => t in BENCH_ATTACHMENT_CONTENT_TYPES, 'unsupported type'),
  contentLength: z.number().int().positive().max(BENCH_ATTACHMENT_MAX_BYTES),
});
export type BenchAttachmentPresignRequest = z.input<typeof benchAttachmentPresignRequestSchema>;

export interface BenchRunAttachmentView {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  createdAt: string;
}

export interface BenchAttachmentPresignResponse {
  id: string;
  upload: { url: string; fields: Record<string, string> };
}

/** GET /api/client/bench/runs/:id/annotations */
export interface BenchRunAnnotations {
  notes: string | null;
  attachments: BenchRunAttachmentView[];
  /** UX only; the DEFINER functions are the gate: the run's own user or an org admin. */
  canEdit: boolean;
}
